import { getLocales } from 'assets/locales/Locale';
import { normalizeCountryCode } from 'assets/utils/helpers/NormalizationHelper';
import { HttpStatus } from 'config/Api.Config';
import { Dictionary, first, groupBy } from 'lodash';
import User from 'models/core/user/Model.User';
import { CountryCode } from 'models/enums/CountryCode';
import LineItem from 'models/enums/LineItem';
import PaymentStatus from 'models/enums/PaymentStatus';
import Model from 'models/Model';
import Claim from '../claim/Model.Claim';
import ClaimJobComment from '../claimJobComment/ClaimJobComment';
import ClaimJobDocument from '../claimJobDocument/ClaimJobDocument';
import ClaimJobItem from '../claimJobItem/Model.ClaimJobItem';
import ClaimJobReport from '../claimJobReport/ClaimJobReport';
import ClaimJobStatusAuditLog from '../claimJobStatusAuditLog/Model.ClaimJobStatusAuditLog';
import ClaimType from '../claimType/Model.ClaimType';
import claimJobChangeOwnershipApi from './changeOwnership/Api.ClaimJob.ChangeOwnership';
import claimJobCloneApi from './clone/Api.ClaimJob.Clone';
import claimJobDeleteApi from './delete/Api.ClaimJob.Delete';
import claimJobFindApi from './find/Api.ClaimJob.Find';
import claimJobListApi from './list/Api.ClaimJob.List';
import claimJobSaveApi from './save/Api.ClaimJob.Save';
import claimJobSetStatusApi from './setStatus/Api.ClaimJob.SetStatus';
import claimJobStatusFlowApi from './statusFlow/Api.ClaimJob.StatusFlow';
import claimJobStatusRequirementsApi from './statusRequirements/Api.ClaimJob.StatusRequirements';
import claimJobTakeOwnershipApi from './takeOwnership/Api.ClaimJob.TakeOwnership';

export default class ClaimJob
  extends Model<Model.IClaimJob>({ icon: 'fas-cloud' })
  implements Model.Instance<Model.IClaimJob>
{
  id: number;
  claimId: number;
  claim?: Claim;
  jobNumber: number;
  statusCode: Model.Enum.ClaimJobStatusCode;
  customerComplaint?: string;
  cause?: string;
  correction?: string;
  currency?: string;
  claimType: ClaimType;
  claimTypeId: number;
  claimTypeVersion: number;
  ownerId?: number;
  owner?: User;
  claimJobComments?: ClaimJobComment[];
  claimJobItems?: ClaimJobItem[];
  claimJobDocuments?: ClaimJobDocument[];
  claimJobReports?: ClaimJobReport[];
  dataPoints?: Model.IClaimJobDataPoint[];
  claimJobStatusAuditLogs?: ClaimJobStatusAuditLog[];
  createdAtUtc?: string;
  modifiedAtUtc?: string;
  authorizationNumber?: string;
  decidedDateUtc?: string;
  paymentDateUtc?: string;
  areAutomationRulesApplied: boolean;

  static aggregates(job: ClaimJob) {
    const groupedItems: Dictionary<ClaimJobItem[]> = groupBy(job?.claimJobItems || [], (it) =>
      it.claimJobItemPayee?.payeeId
        ? `${it.claimJobItemPayee?.payeeId}_payee`
        : it.claimJobItemPayee?.clientId
          ? `${it.claimJobItemPayee?.clientId}_client`
          : it.claimJobItemPayee?.dealerId
            ? `${it.claimJobItemPayee?.dealerId}_dealer`
            : null
    );
    return Object.entries(groupedItems).map(([key, items]) => {
      const payee = first(items).claimJobItemPayee;
      const defaultPaymentMethodCode =
        first(items)?.paymentMethodCode ?? payee?.payee?.paymentMethodCode ?? payee?.dealer?.paymentMethodCode;
      const countryCode =
        first(items)?.claimJobItemPayee?.payee?.countryCode ??
        first(items)?.claimJobItemPayee?.client?.countryCode ??
        first(items)?.claimJobItemPayee?.dealer?.countryCode;

      const payeeCountryCode = normalizeCountryCode(countryCode);

      function sumTax(items: ClaimJobItem[], key: 'gstHst' | 'provincialSalesTax', round = false): number {
        return items
          ?.filter((it) => it.jobItemTypeCode !== LineItem.deductible)
          ?.reduce((prev, curr) => {
            const value = curr.adjudication * curr[key];
            return prev + (round ? Number(value.toFixed(2)) : value);
          }, 0.0);
      }

      return {
        key,
        jobItemId: first(items)?.id,
        jobId: job.id,
        payee,
        contactEmail: payee?.client?.email || payee?.dealer?.primaryEmailAddress || payee?.payee?.email,
        jobItemTypeCode: first(items).jobItemTypeCode,
        currency: payeeCountryCode === CountryCode.CAN ? 'CAD' : 'USD',
        paymentMethodCode: defaultPaymentMethodCode,
        parts: items
          .filter((it) => it.jobItemTypeCode === LineItem.part)
          ?.reduce((prev, curr) => prev + (curr.adjudication || 0), 0.0),
        labour: items
          .filter((it) => it.jobItemTypeCode === LineItem.labour)
          ?.reduce((prev, curr) => prev + (curr.adjudication || 0), 0.0),
        other: items
          .filter((it) => it.jobItemTypeCode === LineItem.other)
          ?.reduce((prev, curr) => prev + (curr.adjudication || 0), 0.0),
        deduct: items
          .filter((it) => it.jobItemTypeCode === LineItem.deductible)
          ?.reduce((prev, curr) => prev + (curr.adjudication || 0), 0.0),
        get ttl() {
          return this.parts + this.labour + this.other;
        },
        gstHst: sumTax(items, 'gstHst'),
        gstHstRounded: sumTax(items, 'gstHst', true),
        provincialSalesTax: sumTax(items, 'provincialSalesTax'),
        provincialSalesTaxRounded: sumTax(items, 'provincialSalesTax', true),
        get totalTaxAmount() {
          return this.gstHst + this.provincialSalesTax;
        },
        get totalTaxAmountRounded() {
          return this.gstHstRounded + this.provincialSalesTaxRounded;
        },
        get totalRounded() {
          return this.ttl - this.deduct + this.totalTaxAmountRounded;
        },
        get total() {
          return this.ttl - this.deduct + this.totalTaxAmount;
        },
        get totalWithoutTaxes() {
          return this.ttl - this.deduct;
        },
        paidAmount: items
          ?.filter((it) => it.statusCode === PaymentStatus.paid)
          ?.reduce(
            (prev, curr) => prev + curr.totalRounded * (curr.jobItemTypeCode !== LineItem.deductible ? 1 : -1),
            0.0
          ),
        usBankVirtualCardId: first(items)?.usBankVirtualCardId,
      };
    });
  }

  constructor(data: Partial<Model.IClaimJob> | Utils.FormData<Model.IClaimJob>, language?: Locale.Languages) {
    super(
      {
        claimType: ClaimType,
        owner: User,
        claimJobComments: [ClaimJobComment],
        claimJobItems: [ClaimJobItem],
        claimJobDocuments: [ClaimJobDocument],
        claimJobReports: [ClaimJobReport],
        claim: Claim,
        claimJobStatusAuditLogs: [ClaimJobStatusAuditLog],
      },
      language
    );
    this.update(data);
  }
  get displayInfo(): Model.DisplayInfo {
    const { lang } = getLocales();
    return {
      id: this.id,
      info: this.statusCode ? lang[this.statusCode]?.replace(/_/g, ' - ') : lang.noStatusDefined,
      title: this.claimType?.displayInfo?.title,
      subtitle: this.owner?.displayInfo?.title || lang.unknownUser,
    };
  }
  static async list(body?: Api.Core.ClaimJob.List.IRequest) {
    const { payload, ...rest } = await claimJobListApi(body);
    return {
      ...rest,
      payload: payload && {
        data: payload.data?.map((it) => new ClaimJob(it)),
        count: payload.count,
      },
    };
  }

  static async find(params: { id: number }) {
    const { payload, ...rest } = await claimJobFindApi(params);
    return {
      ...rest,
      payload: new ClaimJob(payload),
    };
  }
  static async save(data: Api.Core.ClaimJob.Save.IRequest) {
    return await claimJobSaveApi(data);
  }
  async delete() {
    return await claimJobDeleteApi({ id: this.id });
  }
  static async clone(params: { id: number }) {
    return await claimJobCloneApi(params);
  }
  static async setStatus(body: Api.Core.ClaimJob.SetStatus.IRequest) {
    return await claimJobSetStatusApi(body);
  }
  static async changeOwnership(body: Api.Core.ClaimJob.ChangeOwnership.IRequest) {
    return await claimJobChangeOwnershipApi(body);
  }
  static async takeOwnership(body: Api.Core.ClaimJob.TakeOwnership.IRequest) {
    return await claimJobTakeOwnershipApi(body);
  }
  static async getStatusFlowAndRequirements(): Promise<Api.Res<Model.ClaimJobStatusInfo>> {
    const { payload: requirements } = await claimJobStatusRequirementsApi();
    const { payload: flow } = await claimJobStatusFlowApi();
    return {
      status: HttpStatus.ok,
      payload: Object.keys(requirements).reduce(
        (data, status) => ({
          ...data,
          [status]: {
            permissions: requirements[status].ownershipRequirements.permissions,
            nextStatusList: flow[status],
          },
        }),
        {}
      ),
    };
  }
}
